/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import { RadioInput, Typography2, VisuallyHidden, breakpoints, typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import type {
  AllPlansProductsType,
  SelectProductActionType,
} from 'bundles/coursera-plus-common/components/prePurchaseUpsell/PrePurchaseUpsellAllPlansPricingTable';

import _t from 'i18n!nls/coursera-plus';

const styles = {
  cell: () => css`
    border-top: 1px solid lightgray;
    padding: var(--cds-spacing-100);

    label {
      ${typography2.bodySecondary};
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0;
    }
  `,

  selectedCell: css`
    color: var(--cds-color-emphasis-primary-content-default);
    background-color: var(--cds-color-interactive-background-primary-hover-weak);
  `,

  featureCell: () => css`
    ${breakpoints.down('sm')} {
      max-width: 100px;
    }
  `,

  table: css`
    border-collapse: collapse;
    height: 100%;
    width: 100%;

    ${breakpoints.down('xs')} {
      margin: auto;
    }

    label {
      cursor: pointer;
    }
  `,

  header: () => css`
    position: sticky;
    top: 0;
    background: var(--cds-color-neutral-background-primary);

    td {
      border-radius: 5px 5px 0 0;
      height: 90px;
      border: none;
    }

    label {
      flex-direction: column;
      text-align: center;
      gap: var(--cds-spacing-100);
    }

    ${breakpoints.down('xs')} {
      border-bottom: 1px solid var(--cds-color-grey-300);
    }
  `,

  headerWithSubtitle: css`
    tr:first-child td {
      padding-bottom: 0;
    }

    tr:last-child td {
      padding-top: 12px;
    }
  `,
};

type FeatureValue = boolean | string | React.ReactNode;

export type ComparisonTableProduct = {
  id: AllPlansProductsType | string;
  name: string;
  subtitle?: string | React.ReactNode;
  features: Record<string, FeatureValue>;
};

type Props = {
  onSelect?: SelectProductActionType;
  defaultSelectedProduct?: string;
  products: ComparisonTableProduct[];
  features: Record<string, string>;
};

type CellProps = {
  id: string;
  selected: boolean;
  children: React.ReactNode;
};

const Cell = ({ children, id, selected }: CellProps) => (
  <Typography2 variant="bodySecondary" component="td" css={[styles.cell, selected ? styles.selectedCell : undefined]}>
    <label htmlFor={id}>{children}</label>
  </Typography2>
);

const renderFeatureValue = (label: string, value: FeatureValue) => {
  let renderedValue;
  if (typeof value === 'boolean') {
    renderedValue = value ? (
      <CheckIcon size="large" aria-label={_t('Included')} />
    ) : (
      <span aria-label={_t('Not included')}>—</span>
    );
  } else {
    renderedValue = value;
  }

  return (
    <>
      <VisuallyHidden>{label}</VisuallyHidden>
      {renderedValue}
    </>
  );
};

// @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
export const ComparisonTable = ({ products, features, defaultSelectedProduct = products[1].id, onSelect }: Props) => {
  const [selected, setSelected] = useState(defaultSelectedProduct);

  const onChange = (comparisonProductId: AllPlansProductsType) => {
    setSelected(comparisonProductId);
    onSelect?.(comparisonProductId);
  };

  const hasSubtitle = products.some(({ subtitle }) => subtitle);

  return (
    <table css={styles.table}>
      <thead css={[styles.header, hasSubtitle && styles.headerWithSubtitle]}>
        <tr>
          <td>&nbsp;</td>

          {products.map((product) => (
            <Cell key={product.id} id={product.id} selected={selected === product.id}>
              {onSelect && (
                <RadioInput
                  id={product.id}
                  name={product.name}
                  value={product.name}
                  data-testid={`product-option-${product.id}`}
                  checked={selected === product.id}
                  onChange={() => onChange(product.id as AllPlansProductsType)}
                />
              )}
              <Typography2 component="p" variant="actionSecondary">
                {product.name}
              </Typography2>
            </Cell>
          ))}
        </tr>
        {hasSubtitle && (
          <tr>
            <td>&nbsp;</td>
            {products.map((product) => (
              <Cell key={product.id} id={product.id} selected={selected === product.id}>
                {product.subtitle && (
                  <Typography2 component="p" variant="actionSecondary">
                    {product.subtitle}
                  </Typography2>
                )}
              </Cell>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {Object.entries(features).map(([featureKey, featureLabel]) => (
          <tr key={featureKey}>
            <Typography2
              variant="bodySecondary"
              component="td"
              color="supportText"
              css={[styles.cell, styles.featureCell]}
            >
              {featureLabel}
            </Typography2>
            {products.map((product) => (
              <Cell id={product.id} key={product.id} selected={selected === product.id}>
                {renderFeatureValue(featureLabel, product.features[featureKey])}
              </Cell>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ComparisonTable;
